/**
 * What a saved interview's Review tab says, as a coach would: the verdict
 * (the debrief's outcome and the summary), then the moments the person's
 * answer fell short, each with the words they actually said and the records
 * the examiner had read by then, then one list of actions. While the review is being written, or when it
 * failed, the verdict and follow-ups stand and the moments wait. An
 * interview with no review and no debrief has no Review tab: it opens on
 * its transcript.
 *
 * @module components/apps/probe/ProbeReview.logic
 */
import type { ProbeAreaAssessment, ProbeExaminationArea, ProbeOutcome } from "@amoreg/contracts";
import { reviewActionsOf } from "@amoreg/shared/probePractice";

import type { ProbeInterviewDetail } from "../../../state/probeHistory";
import { byImportance, type DeskRecord } from "./desk/deskRecords";

export interface ReviewVerdict {
  /** Null when the summariser wrote no debrief. */
  readonly outcome: ProbeOutcome | null;
  readonly summary: string | null;
}

export interface ReviewMoment {
  readonly area: ProbeExaminationArea;
  /** The transcript line it points at (`data-probe-turn-key`). */
  readonly turnKey: string;
  /** What the person said, as saved. */
  readonly said: string;
  /** Which of the person's answers it was, from 1. */
  readonly answer: number;
  readonly reason: string;
  readonly strongerAnswer: string;
}

/** An area the debrief rated, in the debrief's order. */
export interface RatedArea {
  readonly area: ProbeExaminationArea;
  readonly assessment: ProbeAreaAssessment;
  readonly note: string | null;
}

/** An area the debrief rated weak or adequate, for an interview reviewed before reviews existed. */
export interface ReviewArea {
  readonly area: ProbeExaminationArea;
  readonly note: string | null;
}

export type ReviewBody =
  | { readonly kind: "ready"; readonly moments: ReadonlyArray<ReviewMoment> }
  | { readonly kind: "pending" }
  | { readonly kind: "failed" }
  /** No review was asked for: the debrief's areas to work on stand in for the moments. */
  | { readonly kind: "debrief"; readonly areas: ReadonlyArray<ReviewArea> };

export interface ReviewPage {
  readonly verdict: ReviewVerdict;
  readonly areas: ReadonlyArray<RatedArea>;
  readonly body: ReviewBody;
  /** What the interview left to do, in words. */
  readonly actions: ReadonlyArray<string>;
  /** What "Rehearse again" focuses on: the first moment's area, else the weakest rated; null for none. */
  readonly rehearse: ProbeExaminationArea | null;
}

type ReviewInput = Pick<ProbeInterviewDetail, "summary" | "debrief" | "review" | "turns">;

const weakestRated = (interview: ReviewInput): ProbeExaminationArea | null => {
  const areas = interview.debrief?.areas ?? [];
  return (
    areas.find((entry) => entry.assessment === "weak")?.area ??
    areas.find((entry) => entry.assessment === "adequate")?.area ??
    null
  );
};

/**
 * Each moment against the transcript: the turn whose saved position it
 * names, which must be the person's. A moment whose turn this page no
 * longer holds is dropped rather than shown against the wrong words.
 */
export const reviewMomentsOf = (
  moments: ReadonlyArray<{
    readonly area: ProbeExaminationArea;
    readonly turn: number;
    readonly reason: string;
    readonly strongerAnswer: string;
  }>,
  turns: ProbeInterviewDetail["turns"],
): ReadonlyArray<ReviewMoment> =>
  moments.flatMap((moment) => {
    const index = turns.findIndex((turn) => turn.position === moment.turn);
    const turn = turns[index];
    if (turn === undefined || turn.role !== "user") return [];
    return [
      {
        area: moment.area,
        turnKey: String(index),
        said: turn.text.trim(),
        answer: turns.slice(0, index + 1).filter((one) => one.role === "user").length,
        reason: moment.reason,
        strongerAnswer: moment.strongerAnswer,
      },
    ];
  });

/** The Review tab; null when there is nothing to review and the interview opens on its transcript. */
export const reviewPageOf = (interview: ReviewInput): ReviewPage | null => {
  const { debrief, review } = interview;
  if (review === null && debrief === null) return null;
  const verdict: ReviewVerdict = {
    outcome: debrief?.outcome ?? null,
    summary: interview.summary?.trim() || null,
  };
  const actions = reviewActionsOf(interview);
  const areas: ReadonlyArray<RatedArea> = (debrief?.areas ?? []).map((entry) => ({
    area: entry.area,
    assessment: entry.assessment,
    note: entry.note.trim() || null,
  }));
  if (review === null) {
    const workOn = areas
      .filter((entry) => entry.assessment !== "strong")
      .toSorted((one, other) =>
        one.assessment === other.assessment ? 0 : one.assessment === "weak" ? -1 : 1,
      )
      .map(({ area, note }) => ({ area, note }));
    return {
      verdict,
      areas,
      body: { kind: "debrief", areas: workOn },
      actions,
      rehearse: weakestRated(interview),
    };
  }
  if (review.status !== "ready") {
    return {
      verdict,
      areas,
      body: { kind: review.status },
      actions,
      rehearse: weakestRated(interview),
    };
  }
  const moments = reviewMomentsOf(review.moments, interview.turns);
  return {
    verdict,
    areas,
    body: { kind: "ready", moments },
    actions,
    rehearse: moments[0]?.area ?? weakestRated(interview),
  };
};

/** The kinds a moment shows as in play: what the examiner presses on. */
const IN_PLAY_KINDS: ReadonlySet<DeskRecord["kind"]> = new Set(["finding", "policy"]);

/**
 * The findings and policies in play at a moment: every one the examiner had
 * read under its own lines before the person's answer (`lineRecordsOf`),
 * once, most important first. Read from the lookups, never from the words.
 */
export const recordsInPlay = (
  turnKey: string,
  lineRecords: ReadonlyMap<string, ReadonlyArray<DeskRecord>>,
): ReadonlyArray<DeskRecord> => {
  const answer = Number(turnKey);
  const seen = new Map<string, DeskRecord>();
  for (const [key, records] of lineRecords) {
    if (!(Number(key) < answer)) continue;
    for (const record of records)
      if (IN_PLAY_KINDS.has(record.kind) && !seen.has(record.key)) seen.set(record.key, record);
  }
  return [...seen.values()].toSorted(byImportance);
};
